import { useState, useCallback, useEffect, useRef } from "react";
import type { ElementType } from "react";
import { IconBrandYoutube, IconBrain, IconHelpCircle, IconHeadphones, IconUpload, IconDeviceDesktop, IconBook2, IconFileText, IconX, IconPlus, IconHome, IconNote, IconLayout2Filled, IconPhoto, IconDownload, IconCheck, IconPresentation, IconLayoutSidebar } from "@tabler/icons-react";
import { ChevronLeft, ChevronRight, LibraryBig, Presentation } from "lucide-react";
import { useSidebarContext } from "@/contexts/SidebarContext";
import { DragDropUpload } from "@/components/document/DragDropUpload";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { config } from "@/lib/config";
import { Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { YouTubeUrlModal } from '@/components/youtube/YouTubeUrlModal';
import { ImageSelectionModal } from '@/components/image/ImageSelectionModal';
import { PDFDocument } from 'pdf-lib';
import { YouTubeEmbed, YouTubePlayerRef } from '@/components/youtube/YouTubeEmbed';
import { YouTubeTranscript } from '@/components/youtube/YouTubeTranscript';
import { StudyNoteEditor } from '@/components/notes/StudyNoteEditor';
import { FlashcardGenerationModal } from '@/components/generation/FlashcardGenerationModal';
import { FlashcardViewer } from '@/components/generation/FlashcardViewer';
import { UploadProgressToast } from '@/components/upload/UploadProgressToast';
import Library from './Library';
import LessonView from './LessonView';
import ActivitySlideView from './ActivitySlideView';
import { ActivitySlide } from '@/types/course-outline';
import { Document, LessonSlide } from "@/hooks/useQueryHooks";
import { TopicVideo } from "@/hooks/useTopicVideos";
import { LibraryItem } from "@/hooks/useTopicLibraryItems";

// Configure PDF.js worker
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;
interface Activity {
  id: string;
  title: string;
  icon: ElementType;
  description: string;
}
interface Tab {
  id: string;
  title: string;
  icon?: ElementType;
  content: 'pdf' | 'youtube' | 'note' | 'study-note' | 'flashcards' | 'image-gallery' | 'custom' | 'library' | 'lesson';
  documentId?: string;
  lessonId?: string;
  lessonData?: any;
  activeActivitySlide?: any | null;
  currentSlideIndex?: number;
  allSlides?: any[];
  pdfUrl?: string | null;
  serverPdfUrl?: string; // Backup server URL
  videoId?: string;
  embedUrl?: string;
  noteId?: string;
  noteData?: any; // Store full note data from database
  flashcards?: any[];
  metadata?: any;
  userPrompt?: string;
  isUploading?: boolean;
  localBlobUrl?: string;
  uploadFailed?: boolean;
  transcript?: any[];
  chapters?: any[];
  hasChapters?: boolean;
  images?: {
    id: string;
    file: File;
    blobUrl: string;
    name: string;
  }[];
  currentImageIndex?: number;
  isSaved?: boolean;
  isSaving?: boolean; // Indicator for background saving
}
const activities: Activity[] = [{
  id: 'upload-document',
  title: 'Upload',
  icon: IconUpload,
  description: 'Upload files for AI analysis'
}, {
  id: 'youtube-link',
  title: 'Paste',
  icon: IconBrandYoutube,
  description: 'Learn from YouTube videos'
}, {
  id: 'open-images',
  title: 'Images',
  icon: IconPhoto,
  description: 'Select and view images'
}, {
  id: 'share-screen',
  title: 'Share Screen',
  icon: IconDeviceDesktop,
  description: 'Share screen for tutoring'
}];
interface LessonProgressSummary {
  total: number;
  completed: number;
  pendingTitles: string[];
}

interface CanvasSectionProps {
  openUploadOnMount?: boolean;
  openYouTubeOnMount?: boolean;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onDocumentOpen?: (openFn: (documentId: string, documentName: string) => void) => void;
  onCanvasViewportRef?: (getCanvasEl: () => HTMLElement | null) => void;
  onScreenStreamChange?: (stream: MediaStream | null) => void;
  onYouTubePlayerRef?: (getPlayerRef: () => YouTubePlayerRef | null) => void;
  onYouTubeVideoChange?: (videoId: string | null) => void;
  onVideoAdd?: (videoId: string, embedUrl: string) => void;
  onVideoOpen?: (openFn: (video: any) => void) => void;
  onNoteOpen?: (openFn: (note: any) => void) => void;
  onUploadDocument?: (uploadFn: (file: File) => Promise<void>) => void;
  onAddYouTubeVideo?: (addVideoFn: (videoId: string, title: string, embedUrl: string) => void) => void;
  onOpenImagesWithAutoSaveRef?: (openImagesFn: (images: Array<{id: string; file: File; blobUrl: string; name: string;}>, shouldSave?: boolean) => void) => void;
  hideToolsTab?: boolean;
  onTabsChange?: (hasContentTabs: boolean) => void;
  documents?: Document[];
  videos?: TopicVideo[];
  libraryItems?: LibraryItem[]; // Pre-combined and sorted items
  // Custom selection handlers with study time tracking
  onDocumentSelect?: (documentId: string, documentName: string) => void;
  onVideoSelect?: (video: TopicVideo) => void;
  // Delete handlers
  onDocumentDelete?: (documentId: string, documentName: string) => void;
  onVideoDelete?: (videoId: string, videoName: string) => void;
  onSlideContextChange?: (context: { slide: LessonSlide | null; lessonTitle?: string; progress?: LessonProgressSummary }) => void;
}
const CanvasSection = ({
  openUploadOnMount = false,
  openYouTubeOnMount = false,
  isFullscreen = false,
  onToggleFullscreen,
  onDocumentOpen,
  onCanvasViewportRef,
  onScreenStreamChange,
  onYouTubePlayerRef,
  onYouTubeVideoChange,
  onVideoAdd,
  onVideoOpen,
  onNoteOpen,
  onUploadDocument,
  onAddYouTubeVideo,
  onOpenImagesWithAutoSaveRef,
  hideToolsTab = false,
  onTabsChange,
  documents = [],
  videos = [],
  libraryItems,
  onDocumentSelect,
  onVideoSelect,
  onDocumentDelete,
  onVideoDelete,
  onSlideContextChange = () => {}
}: CanvasSectionProps) => {
  const {
    id: topicId
  } = useParams();
  const {
    user
  } = useAuth();
  const { toggleSidebar, isSidebarCollapsed } = useSidebarContext();
  const queryClient = useQueryClient();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [showFlashcardModal, setShowFlashcardModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isSavingImages, setIsSavingImages] = useState(false);
  const {
    uploadDocument,
    isUploading,
    uploadProgress,
    error,
    isProcessing,
    progressPhase
  } = useDocumentUpload();

  // Upload progress state
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [recentlyUploadedTabs, setRecentlyUploadedTabs] = useState<Set<string>>(new Set());

  // Screen sharing state
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);

  // Create default layout plugin instance
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Tab state - conditionally include home tab
  const [tabs, setTabs] = useState<Tab[]>(hideToolsTab ? [] : [{
    id: 'library',
    title: 'Library',
    icon: LibraryBig,
    content: 'library'
  }]);
  const [activeTabId, setActiveTabId] = useState(hideToolsTab ? '' : 'library');
  const [draggingTabId, setDraggingTabId] = useState<string | null>(null);
  const [isNoteCollapsed, setIsNoteCollapsed] = useState(false);
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null);

  // Reset tabs when topicId changes (when switching topics)
  useEffect(() => {
    if (!hideToolsTab) {
      setTabs([{
        id: 'library',
        title: 'Library',
        icon: LibraryBig,
        content: 'library'
      }]);
      setActiveTabId('library');
    }
  }, [topicId, hideToolsTab]);

  // Canvas viewport ref for vision capture
  const canvasViewportRef = useRef<HTMLDivElement>(null);

const computeLessonProgress = useCallback((slides?: LessonSlide[] | null): LessonProgressSummary | undefined => {
  if (!slides || slides.length === 0) {
    return undefined;
  }
  const total = slides.length;
  const completed = slides.filter(slide => slide.completed).length;
  const pendingTitles = slides
    .filter(slide => !slide.completed)
    .map(slide => slide.title || 'Untitled slide')
    .slice(0, 3);
  return { total, completed, pendingTitles };
}, []);

  // YouTube player ref for auto-pause control
  const youtubePlayerRef = useRef<YouTubePlayerRef>(null);

  // Track documents/videos/notes currently being opened to prevent race conditions
  const openingDocumentsRef = useRef<Set<string>>(new Set());
  const openingVideosRef = useRef<Set<string>>(new Set());
  const openingNotesRef = useRef<Set<string>>(new Set());

  // This will be set up after handleDocumentOpen is defined

  // Function to fetch PDF URL from Supabase
  const fetchPdfUrl = useCallback(async (documentId: string): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    const {
      data: document,
      error: docError
    } = await supabase.from('documents').select('storage_path_original').eq('id', documentId).eq('user_id', user.id).maybeSingle();
    if (docError) throw new Error(`Failed to get document: ${docError.message}`);
    if (!document) throw new Error('Document not found');
    if (!document.storage_path_original) throw new Error('No storage path found for document');
    const {
      data: signedUrl,
      error: urlError
    } = await supabase.storage.from('original-pdfs').createSignedUrl(document.storage_path_original, 3600);
    if (urlError || !signedUrl?.signedUrl) {
      throw new Error(`Failed to get signed URL: ${urlError?.message || 'No URL returned'}`);
    }
    return signedUrl.signedUrl;
  }, [user]);
  const handleUploadDocument = useCallback(async (file: File) => {
    if (!topicId) return;

    // Create a local blob URL for immediate viewing
    const localBlobUrl = URL.createObjectURL(file);

    // Immediately create an optimistic tab with local blob URL
    const optimisticId = `uploading-${Date.now()}`;
    const optimisticTab: Tab = {
      id: optimisticId,
      title: file.name.replace('.pdf', ''),
      icon: IconFileText,
      content: 'pdf',
      documentId: 'uploading',
      pdfUrl: localBlobUrl,
      // Use local blob URL for immediate viewing
      isUploading: true,
      localBlobUrl: localBlobUrl // Store reference for cleanup
    };
    setTabs(prev => [...prev, optimisticTab]);
    setActiveTabId(optimisticId);
    setShowUploadModal(false);

    // Show upload progress
    setUploadingFile(file);
    setShowUploadProgress(true);
    try {
      const result = await uploadDocument(file, topicId);

      // Upload completed successfully - update document ID but keep local URL
      if (result && result.document_id) {
        try {
          const serverPdfUrl = await fetchPdfUrl(result.document_id);
          setTabs(prev => prev.map(tab => {
            if (tab.id === optimisticId) {
              return {
                ...tab,
                documentId: result.document_id,
                // Keep the local blob URL for uninterrupted reading
                pdfUrl: tab.localBlobUrl,
                serverPdfUrl: serverPdfUrl,
                // Store server URL as backup
                isUploading: false
              };
            }
            return tab;
          }));

          // Show success indicator briefly
          setRecentlyUploadedTabs(prev => new Set(prev).add(optimisticId));
          setTimeout(() => {
            setRecentlyUploadedTabs(prev => {
              const newSet = new Set(prev);
              newSet.delete(optimisticId);
              return newSet;
            });
          }, 3000); // Hide after 3 seconds
        } catch (pdfError) {
          console.error('Error fetching server PDF URL:', pdfError);
          // Keep the local blob URL and mark as no longer uploading
          setTabs(prev => prev.map(tab => {
            if (tab.id === optimisticId) {
              return {
                ...tab,
                documentId: result.document_id,
                // Still set the document ID
                isUploading: false
              };
            }
            return tab;
          }));

          // Still show success indicator since upload succeeded
          setRecentlyUploadedTabs(prev => new Set(prev).add(optimisticId));
          setTimeout(() => {
            setRecentlyUploadedTabs(prev => {
              const newSet = new Set(prev);
              newSet.delete(optimisticId);
              return newSet;
            });
          }, 3000);
        }
      }
    } catch (uploadError) {
      console.error('Upload failed:', uploadError);
      // Keep the local version but mark upload as failed
      setTabs(prev => prev.map(tab => {
        if (tab.id === optimisticId) {
          return {
            ...tab,
            isUploading: false,
            uploadFailed: true
          };
        }
        return tab;
      }));
    }
  }, [topicId, uploadDocument, fetchPdfUrl]);
  const handleYouTubeVideoAdd = useCallback((videoId: string, title: string, embedUrl: string) => {
    const newTabId = `youtube-${videoId}`;
    const newTab: Tab = {
      id: newTabId,
      title: title,
      icon: IconBrandYoutube,
      content: 'youtube',
      videoId: videoId,
      embedUrl: embedUrl
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);

    // Save to database if handler is provided
    if (onVideoAdd) {
      onVideoAdd(videoId, embedUrl);
    }
  }, [onVideoAdd]);

  const handleImagesAdd = useCallback((images: { id: string; file: File; blobUrl: string; name: string; }[]) => {
    const newTabId = `images-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: `Images (${images.length})`,
      icon: IconPhoto,
      content: 'image-gallery',
      images: images,
      currentImageIndex: 0
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
    setShowImageModal(false);
  }, []);

  // Helper function to compress and resize image
  const compressImage = useCallback((img: HTMLImageElement, maxWidth: number = 1200, maxHeight: number = 1200, quality: number = 0.8): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    
    // Calculate new dimensions while maintaining aspect ratio
    let { width, height } = img;
    
    if (width > height) {
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
    }
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw and compress the image
    ctx.drawImage(img, 0, 0, width, height);
    
    // Return compressed image as data URL
    return canvas.toDataURL('image/jpeg', quality);
  }, []);

  const handleSaveImagesToLibrary = useCallback(async (images: { id: string; file: File; blobUrl: string; name: string; }[]) => {
    if (!topicId || !images.length) return;

    setIsSavingImages(true);
    
    try {
      // Helper to convert non-PNG/JPEG images to PNG via canvas
      const toPngArrayBuffer = async (file: File): Promise<ArrayBuffer> => {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = URL.createObjectURL(file);
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        
        return new Promise<ArrayBuffer>((resolve) => {
          canvas.toBlob((blob) => {
            blob!.arrayBuffer().then(resolve);
          }, 'image/png', 1);
        });
      };

      // Create PDF using pdf-lib for exact page sizing
      const pdfDoc = await PDFDocument.create();

      for (const item of images) {
        let bytes: ArrayBuffer;
        let kind: 'png' | 'jpg';

        if (item.file.type === 'image/png') {
          bytes = await item.file.arrayBuffer();
          kind = 'png';
        } else if (item.file.type === 'image/jpeg' || item.file.type === 'image/jpg') {
          bytes = await item.file.arrayBuffer();
          kind = 'jpg';
        } else {
          // Convert other formats (e.g., webp) to PNG
          bytes = await toPngArrayBuffer(item.file);
          kind = 'png';
        }

        const image = kind === 'png'
          ? await pdfDoc.embedPng(bytes)
          : await pdfDoc.embedJpg(bytes);

        const width = image.width;
        const height = image.height;

        console.log('PDF page size:', { width, height, name: item.name });

        const page = pdfDoc.addPage([width, height]);
        page.drawImage(image, { x: 0, y: 0, width, height });
      }

      const pdfBytes = await pdfDoc.save({ useObjectStreams: false });
      const pdfBlob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
      
      // Check if PDF size exceeds 32MB limit
      const maxSizeBytes = 32 * 1024 * 1024; // 32MB in bytes
      if (pdfBlob.size > maxSizeBytes) {
        throw new Error(`Generated PDF is too large (${Math.round(pdfBlob.size / 1024 / 1024)}MB). Maximum allowed size is 32MB. Try using fewer images or smaller images.`);
      }
      
      // Create a File object from the blob
      const pdfFile = new File([pdfBlob], `Images_${Date.now()}.pdf`, { type: 'application/pdf' });
      
      // Upload using existing upload function
      const result = await uploadDocument(pdfFile, topicId);
      
      // Mark current tab as saved
      setTabs(prev => prev.map(tab => 
        tab.images === images ? { ...tab, isSaved: true } : tab
      ));
      
    } catch (error) {
      console.error('Error saving images to library:', error);
      // Show user-friendly error message
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert('Failed to save images to library. Please try again.');
      }
    } finally {
      setIsSavingImages(false);
    }
  }, [topicId, uploadDocument]);

  // Open images with optional auto-save for deferred topic creation flow
  const handleOpenImagesWithAutoSave = useCallback((images: { id: string; file: File; blobUrl: string; name: string; }[], shouldSave: boolean = false) => {
    // First: Open the images tab immediately for instant feedback
    const newTabId = `images-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: `Images (${images.length})`,
      icon: IconPhoto,
      content: 'image-gallery',
      images: images,
      currentImageIndex: 0,
      isSaving: shouldSave  // Flag to show saving indicator
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
    
    // Second: If shouldSave flag is true, trigger background save
    if (shouldSave && topicId) {
      console.log('🖼️ Starting background save of images to library');
      
      // Trigger the save in background (don't await - let it run async)
      handleSaveImagesToLibrary(images)
        .then(() => {
          console.log('✅ Images saved to library successfully');
          // Update tab to remove saving indicator
          setTabs(prev => prev.map(tab => 
            tab.id === newTabId 
              ? { ...tab, isSaving: false }
              : tab
          ));
        })
        .catch((error) => {
          console.error('❌ Failed to save images:', error);
          // Update tab to remove saving indicator even on error
          setTabs(prev => prev.map(tab => 
            tab.id === newTabId 
              ? { ...tab, isSaving: false }
              : tab
          ));
        });
    }
  }, [topicId, handleSaveImagesToLibrary]);

  const handleCreateStudyNote = () => {
    const newTabId = `new-note-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: 'New Study Note',
      icon: IconNote,
      content: 'study-note',
      noteId: undefined,
      // No database ID for new notes - let DB generate UUID
      noteData: null
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  };
  const handleFlashcardsGenerated = (flashcards: any[], metadata: any, userPrompt: string) => {
    const flashcardId = `flashcards-${Date.now()}`;
    const newTab: Tab = {
      id: flashcardId,
      title: metadata.topic || 'Flashcards',
      icon: IconBrain,
      content: 'flashcards',
      flashcards: flashcards,
      metadata: metadata,
      userPrompt: userPrompt
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(flashcardId);
  };
  const handleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      stopScreenShare();
      return;
    }
    try {
      // Request screen sharing permission
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: {
            ideal: 1920,
            max: 1920
          },
          height: {
            ideal: 1080,
            max: 1080
          },
          frameRate: {
            ideal: 30,
            max: 30
          }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      setScreenStream(stream);
      setIsScreenSharing(true);

      // Notify parent component about screen stream
      if (onScreenStreamChange) {
        onScreenStreamChange(stream);
      }

      // Handle when user stops sharing from browser controls
      stream.getVideoTracks()[0].addEventListener('ended', () => {
        stopScreenShare();
      });
      // TODO: Send stream to agent when that functionality is implemented
    } catch (error) {
      console.error('Error accessing screen share:', error);
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          alert('Screen sharing permission was denied. Please allow screen sharing to use this feature.');
        } else if (error.name === 'NotSupportedError') {
          alert('Screen sharing is not supported in this browser.');
        } else {
          alert('Failed to start screen sharing. Please try again.');
        }
      }
    }
  };
  const stopScreenShare = () => {
    if (screenStream) {
      // Stop all tracks to end the screen sharing session
      screenStream.getTracks().forEach(track => {
        track.stop();
      });
      setScreenStream(null);
    }
    setIsScreenSharing(false);

    // Notify parent component that screen sharing stopped
    if (onScreenStreamChange) {
      onScreenStreamChange(null);
    }
  };
  const handleDocumentOpen = useCallback(async (documentId: string, documentName: string) => {
    try {
      // Check if tab already exists for this document
      const existingTab = tabs.find(tab => tab.documentId === documentId);
      if (existingTab) {
        setActiveTabId(existingTab.id);
        return;
      }

      // Prevent race condition - check if document is already being opened
      if (openingDocumentsRef.current.has(documentId)) {
        return;
      }

      // Mark document as being opened
      openingDocumentsRef.current.add(documentId);
      try {
        // Fetch PDF URL for the document
        const pdfUrl = await fetchPdfUrl(documentId);
        const newTabId = `pdf-${documentId}`;
        const newTab: Tab = {
          id: newTabId,
          title: documentName.replace('.pdf', ''),
          icon: IconFileText,
          content: 'pdf',
          documentId: documentId,
          pdfUrl: pdfUrl
        };
        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTabId);
      } finally {
        // Always remove from opening set, whether successful or not
        openingDocumentsRef.current.delete(documentId);
      }
    } catch (error) {
      console.error('Error opening document:', error);
      // Ensure we remove from opening set on error
      openingDocumentsRef.current.delete(documentId);
    }
  }, [tabs, setActiveTabId, setTabs, fetchPdfUrl]);

  // Function to handle video opening from video list
  const handleVideoOpen = useCallback((video: any) => {
    try {
      // Create a new video tab with stored data
      const newTabId = `youtube-${video.id}`;

      // Check if tab already exists
      const existingTab = tabs.find(tab => tab.id === newTabId);
      if (existingTab) {
        setActiveTabId(newTabId);
        return;
      }

      // Prevent race condition - check if video is already being opened
      if (openingVideosRef.current.has(video.id)) {
        return;
      }

      // Mark video as being opened
      openingVideosRef.current.add(video.id);
      try {
        const newTab: Tab = {
          id: newTabId,
          title: video.title,
          content: 'youtube',
          videoId: video.video_id,
          embedUrl: video.embed_url,
          transcript: video.transcript,
          chapters: video.chapters,
          hasChapters: video.has_chapters
        };
        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTabId);
      } finally {
        // Always remove from opening set
        openingVideosRef.current.delete(video.id);
      }
    } catch (error) {
      console.error('Error opening video:', error);
      // Ensure we remove from opening set on error
      openingVideosRef.current.delete(video.id);
    }
  }, [tabs, setActiveTabId, setTabs]);

  // Expose upload modal function to parent component
  useEffect(() => {
    if (onUploadDocument) {
      const uploadWithModalControl = Object.assign(handleUploadDocument, {
        openModal: () => setShowUploadModal(true)
      });
      onUploadDocument(uploadWithModalControl);
    }
  }, [onUploadDocument, handleUploadDocument]);

  // Auto-open modals if parameters are true
  useEffect(() => {
    if (openUploadOnMount) {
      setShowUploadModal(true);
    }
    if (openYouTubeOnMount) {
      setShowYouTubeModal(true);
    }
  }, [openUploadOnMount, openYouTubeOnMount]);

  // Expose the handleVideoOpen function to parent component
  useEffect(() => {
    if (onVideoOpen) {
      onVideoOpen(handleVideoOpen);
    }
  }, [onVideoOpen, handleVideoOpen]);

  // Function to handle note opening from note list
  const handleNoteOpen = useCallback((note: any) => {
    try {
      // Create a new note tab using the actual database note ID
      const newTabId = `note-${note.id}`;

      // Check if tab already exists
      const existingTab = tabs.find(tab => tab.id === newTabId);
      if (existingTab) {
        setActiveTabId(newTabId);
        return;
      }

      // Prevent race condition - check if note is already being opened
      if (openingNotesRef.current.has(note.id)) {
        return;
      }

      // Mark note as being opened
      openingNotesRef.current.add(note.id);
      try {
        const newTab: Tab = {
          id: newTabId,
          title: note.title,
          content: 'study-note',
          noteId: note.id,
          // Use the actual database UUID
          noteData: note
        };
        setTabs(prev => [...prev, newTab]);
        setActiveTabId(newTabId);
      } finally {
        // Always remove from opening set
        openingNotesRef.current.delete(note.id);
      }
    } catch (error) {
      console.error('Error opening note:', error);
      // Ensure we remove from opening set on error
      openingNotesRef.current.delete(note.id);
    }
  }, [tabs, setActiveTabId, setTabs]);

  // Expose the handleNoteOpen function to parent component
  useEffect(() => {
    if (onNoteOpen) {
      onNoteOpen(handleNoteOpen);
    }
  }, [onNoteOpen, handleNoteOpen]);

  // Expose the handleDocumentOpen function to parent component
  useEffect(() => {
    if (onDocumentOpen) {
      onDocumentOpen(handleDocumentOpen);
    }
  }, [onDocumentOpen, handleDocumentOpen]);

  // Expose the handleUploadDocument function to parent component
  useEffect(() => {
    if (onUploadDocument) {
      onUploadDocument(handleUploadDocument);
    }
  }, [onUploadDocument, handleUploadDocument]);

  // Expose the handleYouTubeVideoAdd function to parent component
  useEffect(() => {
    if (onAddYouTubeVideo) {
      onAddYouTubeVideo(handleYouTubeVideoAdd);
    }
  }, [onAddYouTubeVideo, handleYouTubeVideoAdd]);

  // Expose the handleOpenImagesWithAutoSave function to parent component
  useEffect(() => {
    if (onOpenImagesWithAutoSaveRef) {
      onOpenImagesWithAutoSaveRef(handleOpenImagesWithAutoSave);
    }
  }, [onOpenImagesWithAutoSaveRef, handleOpenImagesWithAutoSave]);

  // Notify parent about content tabs state
  useEffect(() => {
    if (onTabsChange) {
      onTabsChange(tabs.length > 0);
    }
  }, [tabs, onTabsChange]);

  // Expose the canvas viewport element getter to parent component
  useEffect(() => {
    if (onCanvasViewportRef) {
      onCanvasViewportRef(() => canvasViewportRef.current);
    }
  }, [onCanvasViewportRef]);

  // Expose YouTube player ref to parent component
  useEffect(() => {
    if (onYouTubePlayerRef) {
      onYouTubePlayerRef(() => youtubePlayerRef.current);
    }
  }, [onYouTubePlayerRef]);

  // Track active YouTube video and notify parent
  useEffect(() => {
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    const videoId = activeTab?.content === 'youtube' ? activeTab.videoId : null;
    if (onYouTubeVideoChange) {
      onYouTubeVideoChange(videoId || null);
    }
  }, [activeTabId, tabs, onYouTubeVideoChange]);

  // Cleanup screen sharing on component unmount
  useEffect(() => {
    return () => {
      if (screenStream) {
        screenStream.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, [screenStream]);

  // Generate mock slide content based on activity type
  const generateMockSlideContent = useCallback((type: string): any => {
    switch (type) {
      case 'learn':
        return {
          type: 'learn',
          content: {
            markdown: `Neural networks are computational models inspired by the human brain. They consist of interconnected nodes (neurons) organized in layers.

## Key Components

- **Input Layer**: Receives the raw data
- **Hidden Layers**: Process and transform the data
- **Output Layer**: Produces the final result

Neural networks learn by adjusting the **weights** and **biases** of connections between neurons through a process called training.`,
            imageUrl: '/lovable-uploads/1ca3c391-3e3d-4db3-ab2f-91feb45c3c88.png',
          },
        };
      case 'faq':
        return {
          type: 'faq',
          content: {
            questions: [
              {
                question: 'What is a neural network?',
                answer: 'A neural network is a series of algorithms that endeavors to recognize underlying relationships in a set of data through a process that mimics the way the human brain operates.',
              },
              {
                question: 'How do neural networks learn?',
                answer: 'Neural networks learn through a process called backpropagation, where the network adjusts its weights based on the error of its predictions compared to the actual outcomes.',
              },
              {
                question: 'What are activation functions?',
                answer: 'Activation functions are mathematical equations that determine the output of a neural network node. They help the network learn complex patterns by introducing non-linearity.',
              },
            ],
          },
        };
      case 'quiz':
        return {
          type: 'quiz',
          content: {
            question: 'What is the primary purpose of hidden layers in a neural network?',
            options: [
              'To receive input data',
              'To process and transform data',
              'To produce the final output',
              'To store the training data',
            ],
            correctAnswer: 1,
            explanation: 'Hidden layers are responsible for processing and transforming the input data through weighted connections and activation functions, enabling the network to learn complex patterns.',
          },
        };
      default:
        return { type, content: {} };
    }
  }, []);

  // Handle activity click from lesson view - update the same tab
  const handleActivityClick = useCallback((activity: any) => {
    // activity.content is the LessonSlide from database
    const slideData = activity.content;
    const activeLessonTab = tabs.find(tab => tab.id === activeTabId && tab.content === 'lesson');
    const lessonTitle = activeLessonTab?.title;
    const lessonProgress = computeLessonProgress(activeLessonTab?.allSlides);

    onSlideContextChange({ slide: slideData, lessonTitle, progress: lessonProgress });
    
    // Update the active lesson tab to show the slide
    setTabs(prev => prev.map(tab => {
      if (tab.id === activeTabId && tab.content === 'lesson') {
        return { 
          ...tab, 
          activeActivitySlide: slideData,
          currentSlideIndex: slideData.slide_order - 1 // Convert to 0-based index
        };
      }
      return tab;
    }));
  }, [activeTabId, computeLessonProgress, onSlideContextChange, tabs]);

  // Handle back from activity slide to lesson view
  const handleBackToLesson = useCallback(() => {
    const activeLessonTab = tabs.find(tab => tab.id === activeTabId && tab.content === 'lesson');
    const lessonTitle = activeLessonTab?.title;
    const lessonProgress = computeLessonProgress(activeLessonTab?.allSlides);
    onSlideContextChange({ slide: null, lessonTitle, progress: lessonProgress });

    setTabs(prev => prev.map(tab => 
      tab.id === activeTabId && tab.content === 'lesson'
        ? { ...tab, activeActivitySlide: null, currentSlideIndex: undefined }
        : tab
    ));
  }, [activeTabId, computeLessonProgress, onSlideContextChange, tabs]);

  // Handle slide navigation (next/prev)
  const handleNextSlide = useCallback(() => {
    const activeLessonTab = tabs.find(tab => tab.id === activeTabId && tab.content === 'lesson');
    const lessonProgress = computeLessonProgress(activeLessonTab?.allSlides);
    if (activeLessonTab && activeLessonTab.allSlides && activeLessonTab.allSlides.length > 0) {
      const currentIndex = activeLessonTab.currentSlideIndex || 0;
      const nextIndex = Math.min(currentIndex + 1, activeLessonTab.allSlides.length - 1);
      const nextSlide = activeLessonTab.allSlides[nextIndex];
      onSlideContextChange({ slide: nextSlide, lessonTitle: activeLessonTab.title, progress: lessonProgress });
    }

    setTabs(prev => prev.map(tab => {
      if (tab.id === activeTabId && tab.content === 'lesson' && tab.allSlides) {
        const currentIndex = tab.currentSlideIndex || 0;
        const nextIndex = Math.min(currentIndex + 1, tab.allSlides.length - 1);
        return {
          ...tab,
          currentSlideIndex: nextIndex,
          activeActivitySlide: tab.allSlides[nextIndex]
        };
      }
      return tab;
    }));
  }, [activeTabId, computeLessonProgress, onSlideContextChange, tabs]);

  const handlePrevSlide = useCallback(() => {
    const activeLessonTab = tabs.find(tab => tab.id === activeTabId && tab.content === 'lesson');
    const lessonProgress = computeLessonProgress(activeLessonTab?.allSlides);
    if (activeLessonTab && activeLessonTab.allSlides && activeLessonTab.allSlides.length > 0) {
      const currentIndex = activeLessonTab.currentSlideIndex || 0;
      const prevIndex = Math.max(currentIndex - 1, 0);
      const prevSlide = activeLessonTab.allSlides[prevIndex];
      onSlideContextChange({ slide: prevSlide, lessonTitle: activeLessonTab.title, progress: lessonProgress });
    }

    setTabs(prev => prev.map(tab => {
      if (tab.id === activeTabId && tab.content === 'lesson' && tab.allSlides) {
        const currentIndex = tab.currentSlideIndex || 0;
        const prevIndex = Math.max(currentIndex - 1, 0);
        return {
          ...tab,
          currentSlideIndex: prevIndex,
          activeActivitySlide: tab.allSlides[prevIndex]
        };
      }
      return tab;
    }));
  }, [activeTabId, computeLessonProgress, onSlideContextChange, tabs]);

  // Handle mark slide as complete
  const handleMarkSlideComplete = useCallback(async (slideId: string) => {
    let optimisticContext: { slide: LessonSlide | null; lessonTitle?: string; progress?: LessonProgressSummary } | null = null;

    // Optimistic update
    setTabs(prev => prev.map(tab => {
      if (tab.id === activeTabId && tab.content === 'lesson' && tab.allSlides) {
        const updatedSlides = tab.allSlides.map(slide =>
          slide.id === slideId ? { ...slide, completed: true } : slide
        );
        const nextTab: Tab = {
          ...tab,
          allSlides: updatedSlides,
          activeActivitySlide: tab.activeActivitySlide?.id === slideId
            ? { ...tab.activeActivitySlide, completed: true }
            : tab.activeActivitySlide
        };
        const progressSummary = computeLessonProgress(nextTab.allSlides);
        optimisticContext = {
          slide: nextTab.activeActivitySlide ?? null,
          lessonTitle: nextTab.title,
          progress: progressSummary,
        };
        return nextTab;
      }
      return tab;
    }));
    if (optimisticContext) {
      onSlideContextChange(optimisticContext);
    }
    
    // Call backend to persist
    try {
      const {data: {session}} = await supabase.auth.getSession();
      const token = session?.access_token || '';
      
      const response = await fetch(`${config.BACKEND_API_URL}/api/slides/${slideId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark slide complete');
      }
      
      const result = await response.json();
      console.log('✅ Slide marked complete:', result);
      
      // Invalidate queries to refresh lesson data in Library
      queryClient.invalidateQueries({ queryKey: ['topic-outline', topicId] });
      queryClient.invalidateQueries({ queryKey: ['lesson-with-slides', topicId] });
      
    } catch (error) {
      console.error('Failed to mark slide complete:', error);
      // Revert optimistic update on error
      let revertedContext: { slide: LessonSlide | null; lessonTitle?: string; progress?: LessonProgressSummary } | null = null;
      setTabs(prev => prev.map(tab => {
        if (tab.id === activeTabId && tab.content === 'lesson' && tab.allSlides) {
          const revertedSlides = tab.allSlides.map(slide =>
            slide.id === slideId ? { ...slide, completed: false } : slide
          );
          const nextTab: Tab = {
            ...tab,
            allSlides: revertedSlides,
            activeActivitySlide: tab.activeActivitySlide?.id === slideId
              ? { ...tab.activeActivitySlide, completed: false }
              : tab.activeActivitySlide
          };
          const progressSummary = computeLessonProgress(nextTab.allSlides);
          revertedContext = {
            slide: nextTab.activeActivitySlide ?? null,
            lessonTitle: nextTab.title,
            progress: progressSummary,
          };
          return nextTab;
        }
        return tab;
      }));
      if (revertedContext) {
        onSlideContextChange(revertedContext);
      }
    }
  }, [activeTabId, computeLessonProgress, onSlideContextChange, queryClient, topicId]);

  const addNewTab = () => {
    const newTabId = `tab-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: `New Tab`,
      content: 'custom'
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  };
  const closeTab = (tabId: string) => {
    // Prevent closing the persistent Library tab; allow closing others even if it's the only content tab
    if (!hideToolsTab && tabId === 'library') return;

    const tabToClose = tabs.find(tab => tab.id === tabId);

    // Clean up blob URL for PDF tabs to prevent memory leaks
    if (tabToClose?.localBlobUrl) {
      URL.revokeObjectURL(tabToClose.localBlobUrl);
    }

    // Clean up recently uploaded tabs tracking
    setRecentlyUploadedTabs(prev => {
      const newSet = new Set(prev);
      newSet.delete(tabId);
      return newSet;
    });

    // Clean up localStorage for note tabs if user confirms
    if (tabToClose?.content === 'note' && tabToClose.noteId) {
      const confirmed = window.confirm("Are you sure you want to close this note? Your changes will be saved.");
      if (!confirmed) return;
    }
    setTabs(prev => prev.filter(tab => tab.id !== tabId));

    // If closing active tab, switch to the previous tab
    if (activeTabId === tabId) {
      const currentIndex = tabs.findIndex(tab => tab.id === tabId);
      const remainingTabs = tabs.filter(tab => tab.id !== tabId);
      const newActiveTab = remainingTabs[currentIndex - 1] || remainingTabs[currentIndex] || remainingTabs[0];
      setActiveTabId(newActiveTab?.id || '');
    }
  };

  // Drag & drop reordering (library not draggable)
  const onTabDragStart = (e: React.DragEvent<HTMLDivElement>, tabId: string) => {
    if (tabId === 'library') return;
    setDraggingTabId(tabId);
    e.dataTransfer.setData('text/tab-id', tabId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onTabDragOver = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    if (!draggingTabId || targetId === 'library' || targetId === draggingTabId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTabId(targetId);
  };
  const onTabDragLeave = () => {
    setDragOverTabId(null);
  };
  const onTabDrop = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    const sourceId = e.dataTransfer.getData('text/tab-id') || draggingTabId;
    setDragOverTabId(null);
    setDraggingTabId(null);
    if (!sourceId || sourceId === targetId) return;
    const fromIndex = tabs.findIndex(t => t.id === sourceId);
    const toIndex = tabs.findIndex(t => t.id === targetId);
    if (fromIndex <= 0 || toIndex < 0) return; // prevent moving/dropping on home or invalid
    const insertIndex = toIndex === 0 ? 1 : toIndex;
    const newTabs = [...tabs];
    const [moved] = newTabs.splice(fromIndex, 1);
    newTabs.splice(insertIndex, 0, moved);
    setTabs(newTabs);
  };
  const activeTab = tabs.find(tab => tab.id === activeTabId) || tabs[0];
  return <div ref={canvasViewportRef} className="flex flex-col h-full min-h-0 transition-all duration-300">
      {/* Header with tabs and fullscreen toggle - STICKY TOP */}
      <div className="flex-shrink-0 sticky top-0 z-10 bg-[#F8F8F6] pb-3 pt-3">
        <div className="flex items-center justify-between">
        
        <div className="p-1 rounded-2xl flex bg-[#f0f0ef] border-teal-300 flex-1 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => {
          const IconComp = tab.icon || (tab.content === 'pdf' ? IconFileText : tab.content === 'youtube' ? IconBrandYoutube : tab.content === 'study-note' || tab.content === 'note' ? IconNote : tab.content === 'image-gallery' ? IconPhoto : tab.content === 'lesson' ? Presentation : IconLayout2Filled);
          return <div key={tab.id} title={tab.title} draggable={tab.id !== 'library'} onDragStart={e => onTabDragStart(e, tab.id)} onDragOver={e => onTabDragOver(e, tab.id)} onDragLeave={onTabDragLeave} onDrop={e => onTabDrop(e, tab.id)} className={`flex items-center gap-1 text-sm cursor-pointer transition-all duration-300 ease-out whitespace-nowrap group flex-none overflow-hidden rounded-2xl ${tab.id === 'library' ? 'px-4 py-1.5' : 'px-4 py-1.5 max-w-[140px]'} ${activeTabId === tab.id ? 'bg-background text-foreground shadow-sm animate-scale-in' : 'text-muted-foreground hover:text-foreground'} ${dragOverTabId === tab.id ? 'ring-2 ring-slate-300' : ''}`} onClick={() => setActiveTabId(tab.id)}>
                <IconComp className="w-4 h-4 shrink-0" />
                <span className="font-medium flex-1 min-w-0 truncate">{tab.title}</span>
                {tab.id !== 'library' && <button onClick={e => {
              e.stopPropagation();
              closeTab(tab.id);
            }} className="ml-1 p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-slate-200/50 transition-all duration-200 shrink-0" aria-label={`Close ${tab.title}`}>
                    <IconX className="w-3.5 h-3.5" />
                  </button>}
              </div>;
        })}
        </div>
        
        <div className="flex items-center gap-2">
        {onToggleFullscreen && <button onClick={onToggleFullscreen} className="p-2 text-slate-600 hover:text-slate-900 transition-all duration-200 rounded-lg hover:bg-slate-100" title={isFullscreen ? "Show Sidebar" : "Hide Sidebar"}>
            {isFullscreen ?
          // Exit fullscreen - compress/minimize icon
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5m5.5 11.5V19.5M9 15H4.5M9 15l-5.5 5.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M15 15h4.5M15 15v4.5m0-4.5l5.5 5.5" />
              </svg> :
          // Enter fullscreen - expand icon
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>}
          </button>}
        </div>
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full overflow-auto">
          {!activeTab ? (/* No content when no tabs exist */
        <div className="h-full flex items-center justify-center">
              <div className="text-gray-400 text-xs">No content loaded</div>
            </div>) : activeTab.content === 'pdf' && activeTab.pdfUrl ? (/* PDF Tab Content */
        <div className="h-full">
              <div className="h-full relative">
                {/* Upload status indicator */}
                {activeTab.isUploading && <div className="absolute top-4 right-4 z-10 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                    Uploading...
                  </div>}
                {activeTab.uploadFailed && <div className="absolute top-4 right-4 z-10 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                    Upload failed - viewing locally
                  </div>}
                {/* Show brief success indicator when upload completes */}
                {recentlyUploadedTabs.has(activeTab.id) && <div className="absolute top-4 right-4 z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg animate-pulse">
                    Saved ✓
                  </div>}
                
                {/* PDF Viewer - Always show since we have a URL (local or server) */}
                <Viewer fileUrl={activeTab.pdfUrl} defaultScale={1} plugins={[defaultLayoutPluginInstance]} />
              </div>
            </div>) : activeTab.content === 'youtube' && activeTab.embedUrl && activeTab.videoId ? (/* YouTube Tab Content */
        <div className="h-full flex flex-col p-4">
              {/* Video Player Section */}
              {/* Video Section - Fixed Position */}
              <div className="flex-shrink-0 w-full mb-4 max-w-5xl mx-auto px-2 sm:px-0">
                <div className="relative w-full bg-black rounded-lg overflow-hidden shadow-lg" style={{
              aspectRatio: '16/9'
            }}>
                  <YouTubeEmbed ref={youtubePlayerRef} embedUrl={activeTab.embedUrl} title={activeTab.title} videoId={activeTab.videoId} />
                </div>
              </div>
              
              {/* Transcript Section - Takes remaining space */}
              <div className="flex-1 min-h-0 max-w-5xl mx-auto w-full px-2 sm:px-0">
                <YouTubeTranscript videoId={activeTab.videoId} title={activeTab.title} storedTranscript={activeTab.transcript} storedChapters={activeTab.chapters} hasStoredChapters={activeTab.hasChapters} playerRef={youtubePlayerRef} />
              </div>
            </div>) : activeTab.content === 'note' || activeTab.content === 'study-note' ? (/* Note Tab Content */
        <div className="h-full relative">
              <div className={`h-full absolute left-8 top-0 transition-all duration-300 ${isNoteCollapsed ? 'w-14 min-w-[56px]' : 'min-w-[300px] max-w-[50%] w-[40%]'}`}>
                {/* Collapse/Expand Button */}
                <button onClick={() => setIsNoteCollapsed(!isNoteCollapsed)} className="absolute -right-3 top-4 z-50 p-2 bg-white border-2 border-primary/20 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-primary/5 hover:border-primary/40" title={isNoteCollapsed ? "Expand note" : "Collapse note"}>
                  {isNoteCollapsed ? <ChevronRight className="w-4 h-4 text-primary" /> : <ChevronLeft className="w-4 h-4 text-primary" />}
                </button>

                {/* Note Editor Container */}
                <div className={`h-full transition-opacity duration-300 ${isNoteCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                  <StudyNoteEditor noteId={activeTab.noteId} // Will be undefined for new notes, UUID for existing
              topicId={topicId!} existingNote={activeTab.noteData} initialTitle={activeTab.title} onTitleChange={newTitle => {
                setTabs(tabs.map(t => t.id === activeTab.id ? {
                  ...t,
                  title: newTitle
                } : t));
              }} onNoteSaved={(noteId, title) => {
                // Update tab with new note ID if it was a new note
                setTabs(tabs.map(t => t.id === activeTab.id ? {
                  ...t,
                  noteId,
                  title
                } : t));
              }} onNoteDeleted={() => {
                // Close the tab when note is deleted
                setTabs(tabs.filter(t => t.id !== activeTab.id));
                if (activeTabId === activeTab.id) {
                  const remainingTabs = tabs.filter(t => t.id !== activeTab.id);
                  setActiveTabId(remainingTabs.length > 0 ? remainingTabs[0].id : 'home');
                }
              }} />
                </div>

                {/* Collapsed State - Show minimal note icon */}
                {isNoteCollapsed && <div className="flex items-center justify-center h-full">
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="p-[5px]">
                        <IconNote className="w-5 h-5 text-slate-600" />
                      </div>
                    </div>
                  </div>}
              </div>
            </div>) : activeTab.content === 'flashcards' && activeTab.flashcards && activeTab.metadata ? (/* Flashcard Tab Content */
        <div className="h-full p-6 overflow-auto">
              <FlashcardViewer flashcards={activeTab.flashcards} metadata={activeTab.metadata} userPrompt={activeTab.userPrompt || ''} />
            </div>) : activeTab.content === 'image-gallery' && activeTab.images ? (/* Image Gallery Tab Content */
        <div className="h-full relative">
              {/* Save to Library Button */}
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={() => handleSaveImagesToLibrary(activeTab.images!)}
                  disabled={isSavingImages || activeTab.isSaved}
                  className={`px-3 py-1.5 text-white text-sm font-medium rounded-lg shadow-sm transition-colors flex items-center gap-2 ${
                    activeTab.isSaved 
                      ? 'bg-green-600 cursor-default' 
                      : isSavingImages 
                        ? 'bg-blue-400' 
                        : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {activeTab.isSaved ? (
                    <>
                      <IconCheck className="w-4 h-4" />
                      Saved
                    </>
                  ) : (
                    <>
                      <IconDownload className="w-4 h-4" />
                      {isSavingImages ? 'Saving...' : 'Save to Library'}
                    </>
                  )}
                </button>
              </div>
              
              <div className="h-full overflow-auto p-4">
                <div className="space-y-4">
                  {activeTab.images.map((image, index) => (
                    <div key={image.id}>
                      <img 
                        src={image.blobUrl} 
                        alt={`Image ${index + 1}`}
                        className="w-full rounded-lg shadow-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>) : activeTab.content === 'library' ? (/* Library Tab Content */
        <Library 
          onUploadDocument={handleUploadDocument}
          onYouTubeVideoAdd={handleYouTubeVideoAdd}
          onImagesAdd={handleImagesAdd}
          topicId={topicId}
          onDocumentSelect={handleDocumentOpen}
          onVideoSelect={handleVideoOpen}
          onLessonClick={(lessonData) => {
            // Create new lesson tab with lessonId
            const newTabId = `lesson-${lessonData.id}`;
            
            // Check if tab already exists
            const existingTab = tabs.find(tab => tab.id === newTabId);
            if (existingTab) {
              setActiveTabId(newTabId);
              return;
            }
            
            const newTab: Tab = {
              id: newTabId,
              title: lessonData.title,
              icon: Presentation,
              content: 'lesson',
              lessonId: lessonData.id,
              lessonData: lessonData,
              allSlides: [], // Will be populated when slides are fetched
              currentSlideIndex: 0
            };
            setTabs(prev => [...prev, newTab]);
            setActiveTabId(newTabId);
          }}
        />) : activeTab.content === 'lesson' && activeTab.lessonId ? (
          activeTab.activeActivitySlide ? (
            /* Activity Slide Content within Lesson Tab */
            <ActivitySlideView
              slide={activeTab.activeActivitySlide}
              slideNumber={(activeTab.currentSlideIndex || 0) + 1}
              totalSlides={activeTab.allSlides?.length || 1}
              lessonTitle={activeTab.title}
              lessonThumbnail={activeTab.lessonData?.lesson_image_url}
              onBackToLesson={handleBackToLesson}
              onNext={(activeTab.currentSlideIndex || 0) < (activeTab.allSlides?.length || 1) - 1 ? handleNextSlide : undefined}
              onBack={(activeTab.currentSlideIndex || 0) > 0 ? handlePrevSlide : undefined}
              onMarkComplete={handleMarkSlideComplete}
            />
          ) : (
            /* Lesson Tab Content */
            <LessonView 
              topicId={topicId}
              lessonId={activeTab.lessonId}
              onActivityClick={handleActivityClick}
              onSlidesLoaded={({ lesson, slides }) => {
                setTabs(prev => prev.map(tab =>
                  tab.id === activeTabId && tab.content === 'lesson'
                    ? { ...tab, lessonData: lesson, allSlides: slides }
                    : tab
                ));
                const progressSummary = computeLessonProgress(slides);
                onSlideContextChange({ slide: null, lessonTitle: lesson.title, progress: progressSummary });
              }}
            />
          )
        ) : (/* Other Tab Content */
        <div className="p-6">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-center h-64 text-slate-500 bg-slate-50/30 rounded-lg border border-slate-200/30">
                  <div className="text-center space-y-2">
                    <div className="text-4xl">📄</div>
                    <div className="text-[13px] font-medium">New Tab Content</div>
                    <div className="text-xs">Tab content will be added here</div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Upload Document</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <IconX className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <DragDropUpload onUpload={handleUploadDocument} isUploading={isUploading} />
            
            <div className="mt-4 text-xs text-slate-500 text-center">
              Upload PDF documents to add them to your topic library
            </div>
          </div>
        </div>}

      {/* YouTube URL Modal */}
      <YouTubeUrlModal isOpen={showYouTubeModal} onClose={() => setShowYouTubeModal(false)} onVideoAdd={handleYouTubeVideoAdd} />

      {/* Image Selection Modal */}
      <ImageSelectionModal isOpen={showImageModal} onClose={() => setShowImageModal(false)} onImagesAdd={handleImagesAdd} />

      {/* Flashcard Generation Modal */}
      <FlashcardGenerationModal isOpen={showFlashcardModal} onClose={() => setShowFlashcardModal(false)} onFlashcardsGenerated={handleFlashcardsGenerated} />

              {/* Upload Progress Toast */}
        {showUploadProgress && uploadingFile && <UploadProgressToast fileName={uploadingFile.name} progress={uploadProgress} isUploading={isUploading} isProcessing={isProcessing} progressPhase={progressPhase} error={error} onClose={() => {
      setShowUploadProgress(false);
      setUploadingFile(null);
    }} />}
    </div>;
};
export default CanvasSection;
